import type { Dispatch, FC, PropsWithChildren } from 'react'
import { createContext, useContext, useMemo, useReducer } from 'react'

import { useLocation } from 'react-router-dom'

import type { DrillDownType } from '@/modules/admin/resources'

import {
  DrillDownContextActionType,
  type DrillDownContextAction,
  type DrillDownContextState,
} from './types.context'

const CURRENT_YEAR = new Date().getFullYear()
const CURRENT_MONTH = new Date().getMonth()

export const DEFAULT_TYPE: DrillDownType = 'frame-agreement'
export const DEFAULT_TYPE_ID = 0
export const DEFAULT_DRILL_DOWN_YEAR = CURRENT_YEAR
export const DEFAULT_DRILL_DOWN_PERIOD = CURRENT_MONTH === 0 ? -5 : CURRENT_MONTH

export const DRILL_DOWN_YEAR_OPTIONS = Array.from({ length: 5 }, (v, i) => ({
  value: String(CURRENT_YEAR - i),
  label: String(CURRENT_YEAR - i),
}))

const initialState: DrillDownContextState = {
  year: String(DEFAULT_DRILL_DOWN_YEAR),
  period: String(DEFAULT_DRILL_DOWN_PERIOD),
  search: '',
  currency: '',
  type: null,
  typeId: null,
  productCategoryId: null,
  productGroupId: null,
  productId: null,
  departmentId: null,
  costObjectId: null,
}

const reducer = (state: DrillDownContextState, action: DrillDownContextAction) => {
  switch (action.type) {
    case DrillDownContextActionType.SET_QUERY_YEAR:
      return {
        ...state,
        year: action.payload,
      }
    case DrillDownContextActionType.SET_QUERY_PERIOD:
      return {
        ...state,
        period: action.payload,
      }
    case DrillDownContextActionType.SET_QUERY_SEARCH:
      return {
        ...state,
        search: action.payload,
      }
    case DrillDownContextActionType.SET_QUERY_CURRENCY:
      return {
        ...state,
        currency: action.payload,
      }
    case DrillDownContextActionType.SET_QUERY_TYPE:
      return {
        ...state,
        type: action.payload,
      }
    case DrillDownContextActionType.SET_QUERY_TYPE_ID:
      return {
        ...state,
        typeId: action.payload,
      }
    case DrillDownContextActionType.SET_PRODUCT_CATEGORY_ID:
      return {
        ...state,
        productCategoryId: action.payload,
      }
    case DrillDownContextActionType.SET_PRODUCT_GROUP_ID:
      return {
        ...state,
        productGroupId: action.payload,
      }
    case DrillDownContextActionType.SET_PRODUCT_ID:
      return {
        ...state,
        productId: action.payload,
      }
    case DrillDownContextActionType.SET_DEPARTMENT_ID:
      return {
        ...state,
        departmentId: action.payload,
      }
    case DrillDownContextActionType.SET_COST_OBJECT_ID:
      return {
        ...state,
        costObjectId: action.payload,
      }
    case DrillDownContextActionType.SET_MULTIPLE_DRILL_DOWN:
      return {
        ...state,
        ...action.payload,
      }
    default:
      return state
  }
}

const DrillDownContext = createContext<{
  state: DrillDownContextState
  dispatch: Dispatch<DrillDownContextAction>
}>({
  state: initialState,
  dispatch: () => null,
})

export const useDrillDownContext = () => {
  const context = useContext(DrillDownContext)
  if (!context) {
    throw new Error('useDrillDownContext must be used within a DrillDownContextProvider')
  }
  return context
}

export const DrillDownContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const params = useSearchParams({
    year: String(DEFAULT_DRILL_DOWN_YEAR),
    period: String(DEFAULT_DRILL_DOWN_PERIOD),
    search: '',
    currency: '',
    type: DEFAULT_TYPE,
    typeId: '',
    productCategoryId: '',
    productGroupId: '',
    productId: '',
    departmentId: '',
    costObjectId: '',
  })

  const [state, dispatch] = useReducer(reducer, { ...initialState, ...params })

  const value = useMemo(() => ({ state, dispatch }), [state, dispatch])

  return <DrillDownContext.Provider value={value}>{children}</DrillDownContext.Provider>
}

type SearchParamsInput = {
  [queryString: string]: string | undefined
}

export const useSearchParams = (input: SearchParamsInput) => {
  const location = useLocation()

  const searchParams = new URLSearchParams(location.search)

  return Object.keys(input).reduce((acc, key) => {
    const value = searchParams.get(key) || input[key]
    return {
      ...acc,
      [key]: value,
    }
  }, {})
}
